import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { ZoomIn, ZoomOut, Maximize, Download, ExternalLink, Loader2, AlertTriangle } from 'lucide-react';

pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.mjs`;

const MIN_ZOOM = 0.5, MAX_ZOOM = 4, STEP = 0.25;
const clamp = (z) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z));
const DEFAULT_LABELS = { zoomIn: 'Zoom in', zoomOut: 'Zoom out', fit: 'Fit to width', download: 'Download', openTab: 'Open in new tab', page: (a, b) => `${a} / ${b}`, loading: 'Loading PDF…', error: 'Could not load the PDF.', scrollHint: 'Scroll to the end to continue' };

export const downloadUrl = async (src, filename) => {
  const blob = await (await fetch(src, { credentials: 'same-origin' })).blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename || 'document.pdf'; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
};

// Visualizador de PDF: todas as páginas, zoom por botões / pinça (2 dedos) / Ctrl+scroll, arrastar para mover,
// indicador de página, exportar (download) e abrir em nova aba. onReachEnd dispara quando o usuário chega ao fim do documento.
export const PdfViewer = ({ src, filename = 'document.pdf', labels: lb, onReachEnd, extraActions, showOpenTab = true, className = '', testId = 'pdf-viewer' }) => {
  const labels = { ...DEFAULT_LABELS, ...(lb || {}) };
  const scroller = useRef(null);
  const pointers = useRef(new Map());
  const pinch = useRef(null);
  const pan = useRef(null);
  const reached = useRef(false);
  const [baseWidth, setBaseWidth] = useState(600);
  const [zoom, setZoom] = useState(1);
  const [live, setLiveState] = useState(1);
  const liveRef = useRef(1);
  const setLive = (v) => { liveRef.current = v; setLiveState(v); };
  const [numPages, setNumPages] = useState(0);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('loading');
  const file = useMemo(() => ({ url: src }), [src]);

  useEffect(() => {
    const el = scroller.current;
    if (!el) return undefined;
    const measure = () => setBaseWidth(Math.max(240, Math.min(el.clientWidth - 24, 900)));
    measure();
    const ro = new ResizeObserver(measure); ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const checkEnd = useCallback(() => {
    const el = scroller.current;
    if (!el || !numPages || status !== 'ready' || reached.current) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 12) { reached.current = true; onReachEnd?.(); }
  }, [numPages, status, onReachEnd]);

  const onScroll = () => {
    const el = scroller.current;
    if (!el) return;
    const top = el.getBoundingClientRect().top;
    const mid = el.clientHeight / 3;
    let cur = 1;
    [...el.querySelectorAll('[data-page]')].forEach((p, i) => { if (p.getBoundingClientRect().top - top <= mid) cur = i + 1; });
    setPage(cur);
    checkEnd();
  };
  useEffect(() => { const id = setTimeout(checkEnd, 600); return () => clearTimeout(id); }, [checkEnd, zoom, baseWidth]);

  const commitZoom = (next, cx, cy) => {
    const el = scroller.current;
    const z = clamp(next);
    if (el) {
      const ratio = z / zoom;
      const x = cx ?? el.clientWidth / 2, y = cy ?? el.clientHeight / 2;
      requestAnimationFrame(() => { el.scrollLeft = (el.scrollLeft + x) * ratio - x; el.scrollTop = (el.scrollTop + y) * ratio - y; });
    }
    setZoom(z); setLive(1);
  };

  const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
  const onPointerDown = (e) => {
    const el = scroller.current;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    try { el.setPointerCapture?.(e.pointerId); } catch { /* ponteiro sintético/inativo */ }
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      pinch.current = { d0: dist(a, b), z0: zoom, cx: (a.x + b.x) / 2 - el.getBoundingClientRect().left, cy: (a.y + b.y) / 2 - el.getBoundingClientRect().top };
      pan.current = null;
    } else if (pointers.current.size === 1) {
      pan.current = { x: e.clientX, y: e.clientY, sl: el.scrollLeft, st: el.scrollTop, moved: false };
    }
  };
  const onPointerMove = (e) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const el = scroller.current;
    if (pinch.current && pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      const target = clamp(pinch.current.z0 * (dist(a, b) / pinch.current.d0));
      setLive(target / zoom);
    } else if (pan.current && pointers.current.size === 1) {
      const dx = e.clientX - pan.current.x, dy = e.clientY - pan.current.y;
      if (Math.abs(dx) + Math.abs(dy) > 3) pan.current.moved = true;
      el.scrollLeft = pan.current.sl - dx; el.scrollTop = pan.current.st - dy;
    }
  };
  const onPointerUp = (e) => {
    pointers.current.delete(e.pointerId);
    if (pinch.current && pointers.current.size < 2) {
      const { cx, cy } = pinch.current; pinch.current = null;
      if (liveRef.current !== 1) commitZoom(zoom * liveRef.current, cx, cy);
    }
    if (pointers.current.size === 0) pan.current = null;
  };
  const onWheel = (e) => {
    if (!(e.ctrlKey || e.metaKey)) return;
    e.preventDefault();
    const r = scroller.current.getBoundingClientRect();
    commitZoom(zoom * (e.deltaY < 0 ? 1.1 : 0.9), e.clientX - r.left, e.clientY - r.top);
  };
  useEffect(() => {
    const el = scroller.current;
    if (!el) return undefined;
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  });

  const btn = 'h-10 w-10 rounded-full flex items-center justify-center text-slate-700 hover:bg-slate-900/5 disabled:opacity-30 transition-colors';
  return (
    <div className={`flex flex-col min-h-0 ${className}`} data-testid={testId}>
      <div className="flex items-center justify-between gap-2 px-2 py-1.5 border-b border-slate-200/70 bg-white/80 backdrop-blur shrink-0" data-testid={`${testId}-toolbar`}>
        <div className="flex items-center gap-0.5">
          <button type="button" onClick={() => commitZoom(zoom - STEP)} disabled={zoom <= MIN_ZOOM} className={btn} aria-label={labels.zoomOut} data-testid={`${testId}-zoom-out`}><ZoomOut className="h-5 w-5" /></button>
          <span className="min-w-12 text-center text-xs font-bold tabular-nums text-slate-700" data-testid={`${testId}-zoom-level`}>{Math.round(zoom * live * 100)}%</span>
          <button type="button" onClick={() => commitZoom(zoom + STEP)} disabled={zoom >= MAX_ZOOM} className={btn} aria-label={labels.zoomIn} data-testid={`${testId}-zoom-in`}><ZoomIn className="h-5 w-5" /></button>
          <button type="button" onClick={() => commitZoom(1)} disabled={zoom === 1} className={btn} aria-label={labels.fit} data-testid={`${testId}-zoom-fit`}><Maximize className="h-5 w-5" /></button>
        </div>
        <span className="text-xs font-semibold text-slate-600 tabular-nums" data-testid={`${testId}-page`}>{numPages ? labels.page(page, numPages) : ''}</span>
        <div className="flex items-center gap-0.5">
          {extraActions}
          <button type="button" onClick={() => downloadUrl(src, filename)} className={btn} aria-label={labels.download} title={labels.download} data-testid={`${testId}-download`}><Download className="h-5 w-5" /></button>
          {showOpenTab && <button type="button" onClick={() => window.open(src, '_blank', 'noopener')} className={btn} aria-label={labels.openTab} title={labels.openTab} data-testid={`${testId}-open-tab`}><ExternalLink className="h-5 w-5" /></button>}
        </div>
      </div>
      <div ref={scroller} onScroll={onScroll} onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp}
        className="flex-1 min-h-0 overflow-auto bg-slate-200/70 sales-kanban-scroll cursor-grab active:cursor-grabbing" style={{ touchAction: 'none', overscrollBehavior: 'contain' }} data-testid={`${testId}-scroller`}>
        <div className="p-3 origin-top-left" style={{ transform: live !== 1 ? `scale(${live})` : undefined, width: live !== 1 ? `${100 / live}%` : undefined }}>
          <Document file={file} loading={null} error={null} onLoadSuccess={({ numPages: n }) => { setNumPages(n); setStatus('ready'); }} onLoadError={() => setStatus('error')}>
            {Array.from({ length: numPages }, (_, i) => (
              <div key={i} data-page={i + 1} className="mx-auto mb-3 shadow-lg rounded-sm overflow-hidden bg-white" style={{ width: Math.round(baseWidth * zoom) }}>
                <Page pageNumber={i + 1} width={Math.round(baseWidth * zoom)} renderTextLayer={false} renderAnnotationLayer={false} loading={<div style={{ height: Math.round(baseWidth * zoom * 1.29) }} />} />
              </div>
            ))}
          </Document>
          {status === 'loading' && <div className="flex items-center justify-center gap-2 py-16 text-sm font-semibold text-slate-600" data-testid={`${testId}-loading`}><Loader2 className="h-5 w-5 animate-spin" /> {labels.loading}</div>}
          {status === 'error' && <div className="flex items-center justify-center gap-2 py-16 text-sm font-semibold text-rose-700" data-testid={`${testId}-error`}><AlertTriangle className="h-5 w-5" /> {labels.error}</div>}
        </div>
      </div>
    </div>
  );
};
